import { ReactNode } from 'react';
import {
  Badge, Button, Card, Group, Skeleton, Stack, Text, ThemeIcon, UnstyledButton,
} from '@mantine/core';
import { IconArrowRight, TablerIconsProps } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { brandBadgeStyle, brandSolidStyle } from '@/utils/color';
import { badgeStyles } from '@/utils/tableStyles';
import { CRITICAL_LIST_SIZE } from '../constants';

/**
 * CriticalListCard — a short "these need attention" list with a route out to the full page.
 *
 * Deliberately not a `CCMSDataTable`: at five rows a table's header and column machinery
 * costs more than it explains. Each row is a two-line summary plus one badge, and the whole
 * row is the click target — matching the row-click convention on the real list pages.
 */
export interface CriticalRow {
  id: number | string;
  /** Primary line — the thing itself. */
  title: string;
  /** Secondary line — who/when/why. */
  meta?: string;
  /** Right-hand badge, e.g. status or age. */
  badge?: string;
  /** Badge background; the label colour is derived for contrast. */
  badgeColor?: string;
  /** Route for this specific row. Falls back to the card's `viewAllTo`. */
  to?: string;
}

export interface CriticalListCardProps {
  title: string;
  description?: string;
  icon: React.ComponentType<TablerIconsProps>;
  /** Accent for the header icon — use a warning/danger tone for genuine backlogs. */
  color?: string;
  rows: CriticalRow[];
  loading?: boolean;
  /** Total behind the list, when more rows exist than are shown. */
  totalCount?: number;
  viewAllTo?: string;
  /** Used instead of navigation when the destination page doesn't exist yet. */
  onViewAll?: () => void;
  emptyMessage?: string;
  /** Rendered in place of the row list — for cards that need a custom body. */
  children?: ReactNode;
}

export default function CriticalListCard({
  title,
  description,
  icon: Icon,
  color = 'var(--mantine-primary-color-filled)',
  rows,
  loading = false,
  totalCount,
  viewAllTo,
  onViewAll,
  emptyMessage = 'Nothing needs attention here.',
  children,
}: CriticalListCardProps) {
  const navigate = useNavigate();
  const shown = rows.slice(0, CRITICAL_LIST_SIZE);
  const more = (totalCount ?? rows.length) - shown.length;

  const handleViewAll = () => {
    if (onViewAll) return onViewAll();
    if (viewAllTo) navigate(viewAllTo);
  };

  return (
    <Card withBorder radius="md" padding="lg" h="100%">
      <Group justify="space-between" align="flex-start" mb="md" wrap="nowrap">
        <Group gap="sm" wrap="nowrap">
          {/* Filled, so the glyph keeps contrast on both themes — see `brandSolidStyle`. */}
          <ThemeIcon size={38} radius="md" variant="filled" style={brandSolidStyle(color)}>
            <Icon size={20} />
          </ThemeIcon>
          <div>
            <Group gap={6} wrap="nowrap">
              <Text fw={600}>{title}</Text>
              {totalCount !== undefined && totalCount > 0 && (
                <Badge size="sm" variant="light">{totalCount}</Badge>
              )}
            </Group>
            {description && (
              <Text size="xs" c="dimmed">{description}</Text>
            )}
          </div>
        </Group>

        {(viewAllTo || onViewAll) && (
          <Button
            variant="subtle"
            size="compact-xs"
            rightSection={<IconArrowRight size={13} />}
            onClick={handleViewAll}
          >
            View all
          </Button>
        )}
      </Group>

      {children ?? (
        loading && rows.length === 0 ? (
          <Stack gap="xs">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} height={38} radius="sm" />
            ))}
          </Stack>
        ) : shown.length === 0 ? (
          <Text size="sm" c="dimmed">{emptyMessage}</Text>
        ) : (
          <Stack gap={4}>
            {shown.map((row) => {
              const body = (
                <Group justify="space-between" wrap="nowrap" py={6} px={8}>
                  <div style={{ minWidth: 0 }}>
                    <Text size="sm" fw={500} truncate>{row.title}</Text>
                    {row.meta && (
                      <Text size="xs" c="dimmed" truncate>{row.meta}</Text>
                    )}
                  </div>
                  {row.badge && (
                    <Badge
                      size="sm"
                      variant="filled"
                      style={brandBadgeStyle(row.badgeColor)}
                      styles={badgeStyles}
                    >
                      {row.badge}
                    </Badge>
                  )}
                </Group>
              );

              const target = row.to ?? viewAllTo;
              if (!target) return <div key={row.id}>{body}</div>;

              return (
                <UnstyledButton
                  key={row.id}
                  onClick={() => navigate(target)}
                  style={{ borderRadius: 6 }}
                >
                  {body}
                </UnstyledButton>
              );
            })}

            {more > 0 && (
              <Text size="xs" c="dimmed" mt={4} px={8}>
                +{more} more
              </Text>
            )}
          </Stack>
        )
      )}
    </Card>
  );
}
